import {Injectable, NgZone} from '@angular/core';
import { GeoLocation } from '../models/geo-location';
import { Address } from '../models/address';
import {BehaviorSubject} from 'rxjs';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {TranslateService} from '@ngx-translate/core';
import {LocationDeniedDialogComponent} from '../components/location-denied-dialog/location-denied-dialog.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ScannerService} from './scanner.service';
import {environment} from '../../environments/environment';
import {isiOS} from '../utils';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  state: BehaviorSubject<GeoLocation | 'not-permitted' | 'pending'> = new BehaviorSubject('pending');

  private lastGeoPosition?: GeolocationPosition;

  // last time we showed the permission denied nag popup
  private modalLastShownDate?: Date;

  // ID of the task watching the location, null meaning no watchPosition() is currently running
  private watchHandlerId: number | null = null;

  // the currently showing 'location nag popup', null if not currently shown
  private dialogRef: MatDialogRef<LocationDeniedDialogComponent, void> | null = null;

  constructor(private translate: TranslateService,
              private scanner: ScannerService,
              private matDialog: MatDialog,
              private ngZone: NgZone,
              auth: AngularFireAuth) {

    // tie lifecycle of this service to auth
    auth.authState.subscribe(authState => {
      if (authState === null) { // logged out, state is 'off'
        this.state.next('pending');

        // clear a pending watch
        //this.stopWatchingPosition();
        console.debug(`[location] logged out, not watching position`);
      } else { // logged in, start watching position
        console.debug(`[location] logged in, start watching position`);
        navigator.permissions.query({ name: 'geolocation' })
          .then((permissionStatus) => {
            const updateStateFromPermissionChange = (state: string) => {
              this.ngZone.run(() => {
                if (isiOS()) {
                  // special treatment for iOS, which always returns 'prompt' even if user selects 'Allow'
                  if (state === 'denied') {
                    this.state.next('not-permitted');
                  } else {
                    this.state.next('pending');
                  }
                } else {
                  // regular handling
                  if (state === 'denied' || state === 'prompt') {
                    this.state.next('not-permitted');
                  } else {
                    this.state.next('pending');
                  }
                }
              });
            }
            permissionStatus.onchange = () => {
              updateStateFromPermissionChange(permissionStatus.state);
            };
            updateStateFromPermissionChange(permissionStatus.state)
          });
      }
    });
  }

  public get lastPosition(): GeoLocation {
    if (this.lastGeoPosition === undefined) {
      return { latitude: 0.0, longitude: 0.0 }; // placeholder
    } else {
      // TODO: check timestamp if stale?
      return {
        latitude: this.lastGeoPosition.coords.latitude,
        longitude: this.lastGeoPosition.coords.longitude
      }
    }
  }

  getLocalizedErrorMessage(err: Error): string {
    if (err instanceof GeolocationPositionError) {
      // https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
      if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
        return this.translate.instant('locationErrors.permissionDenied');
      } else if (err.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
        return this.translate.instant('locationErrors.notAvailable');
      } else if (err.code === GeolocationPositionError.TIMEOUT) {
        return this.translate.instant('locationErrors.timeout');
      }
    }
    // fallback error message
    return this.translate.instant('locationErrors.generic');
  }

  /**
   * @returns The current location of the user. Will return (0, 0) if no location is known yet.
   */
  get currentLocation(): GeoLocation {
    return this.lastPosition;
  }

  showDirectionsTo(address: Address): void {
    const routeUri = this.getRouteURITo(address);
    window.open(routeUri, '_blank');
  }

  startWatchingPosition(): void {

    // clear any pending watches
    this.stopWatchingPosition();

    // start watching position
    this.watchHandlerId = navigator.geolocation.watchPosition(
      (position: GeolocationPosition) => {

        // got a position update
        this.lastGeoPosition = position;
        this.ngZone.run(() => {
          this.state.next(this.lastPosition);
        });
        console.debug(`[location-${this.watchHandlerId}] Updated position (${this.lastPosition.latitude}, ${this.lastPosition.longitude}), accuracy: ${position.coords.accuracy}`);
      },
      (error: GeolocationPositionError) => {

        // got an error
        console.error(`[location-${this.watchHandlerId}] Encountered error ${error.code}: ${error.message}`);
        if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
          this.ngZone.run(() => {
            this.state.next('not-permitted');
          });
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 5_000
      }
    );
    console.debug(`[location] started new watch: ${this.watchHandlerId}`);
  }

  stopWatchingPosition(): void {
    if (this.watchHandlerId !== null) {
      navigator.geolocation.clearWatch(this.watchHandlerId);
      console.debug(`[location-${this.watchHandlerId}] And now, my watch is ended 🐺`);
      this.watchHandlerId = null;
    }
  }

  isLocationPermissionDenied(): boolean {
    return this.state.value === 'not-permitted';
  }

  showLocationDeniedModal(force: boolean) {

    // check if we're already showing the dialog
    if (this.dialogRef !== null) {
      return;
    }

    const doShowLocationDeniedModal = () => {

      // pause scanner when the dialog is showing
      this.scanner.pauseScanning();

      // show the dialog
      this.dialogRef = this.matDialog.open(LocationDeniedDialogComponent, {
        minWidth: '240px',
        maxWidth: '320px'
      });

      // resume scanning when the dialog is closed
      this.dialogRef.afterClosed().subscribe(() => {
        this.scanner.resumeScanning();
        this.dialogRef = null;
      });
    };

    // only show the dialog if we haven't shown it in an interval
    const nowDate = new Date();
    if (this.modalLastShownDate === undefined || force) {
      console.debug(`[location] showing modal (no last shown date, or force)`);
      doShowLocationDeniedModal();
      this.modalLastShownDate = nowDate;
    } else {
      const lastShownMillisAgo = nowDate.getTime() - this.modalLastShownDate.getTime();
      if (lastShownMillisAgo > environment.locationNagIntervalMs) {
        console.debug(`[location] showing modal dialog, last shown: ${lastShownMillisAgo}ms ago`);
        doShowLocationDeniedModal();
        this.modalLastShownDate = nowDate;
      } else {
        console.debug(`[location] not showing modal dialog, last shown ${lastShownMillisAgo}ms ago`);
      }
    }
  }

  /**
   * On entry to a screen that requires location. Will attempt to get a fix for the current location
   *
   * Also called on app visibility events ('visible')
   */
  async updateCurrentLocation(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      console.debug('[location] calling getCurrentPosition');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.debug(`[location] getCurrentPosition succeeded: ${position.coords.latitude}, ${position.coords.longitude}`);
          this.lastGeoPosition = position;
          this.state.next(this.lastPosition);
          resolve(position);
        },
        (error) => {
          console.error(`[location] getCurrentPosition failed (${error.code}): ${error.message}`);
          if (error.code === GeolocationPositionError.TIMEOUT) { // timeout
            if (this.lastGeoPosition === undefined) {
              this.state.next('pending');
            } else {
              // keep current position
            }
          } else if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
            this.state.next('not-permitted');
          } else if (error.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
            if (this.lastGeoPosition === undefined) {
              this.state.next('pending');
            } else {
              // keep current position
            }
          }
          reject(error);
        },
        {
          enableHighAccuracy: false,
          timeout: 5_000 // 5 seconds
        });
    });
  }

  /**
   * Get the intent URI for showing a route between two points
   *
   * @see https://developers.google.com/maps/documentation/urls/guide#directions-action
   *
   * @param end Ending point
   * @returns Intent URI
   */
  private getRouteURITo(end: Address | GeoLocation): string {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    if (isIOS) {
      return `https://maps.apple.com/?daddr=${this.querize(end)}`;
    } else {
      return `https://maps.google.com/maps?daddr=${this.querize(end)}`;
    }
  }

  /**
   * Get the Google Maps query supported string from an Address
   *
   * @see https://developers.google.com/maps/documentation/urls/android-intents#uri_encoded_query_strings
   *
   * @param point Address
   * @returns Google Maps query string
   */
  private querize(point: Address | GeoLocation): string {
    const address = point as Address;
    const location = point as GeoLocation;
    if (address.street || address.streetNumber || address.zipCode || address.city) {
      return (window as any).encodeURIComponent(
        `${address.street} ${address.streetNumber}, ${address.zipCode} ${address.city}`);
    } else if (location.latitude && location.longitude) {
      return `${location.latitude},${location.longitude}`;
    } else {
      return '';
    }
  }
}
